import React, { useId, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Step,
    StepLabel,
    Stepper,
    Switch,
    TextField,
    Typography
} from '@mui/material';

type UnleashedIntegrationSettingsProps = {
    activeStep: number;
    steps: string[];
    apiId: string;
    apiKey: string;
    integrationEnabled: boolean;
    canEnableIntegration: boolean;
    savingSettings: boolean;
    loadingProductGroups: boolean;
    loadingSalesOrderGroups: boolean;
    autoDisabledMessage: string | null;
    productGroups: string[];
    selectedProductGroups: string[];
    allProductsSelected: boolean;
    includeNonSellable: boolean;
    salesOrderGroups: string[];
    selectedSalesOrderGroups: string[];
    allSalesOrderGroupsSelected: boolean;
    onApiIdChange: (value: string) => void;
    onApiKeyChange: (value: string) => void;
    onToggleEnabled: (checked: boolean) => void;
    onAllProductsChange: (checked: boolean) => void;
    onProductGroupsChange: (value: string[]) => void;
    onIncludeNonSellableChange: (checked: boolean) => void;
    onAllSalesOrderGroupsChange: (checked: boolean) => void;
    onSalesOrderGroupsChange: (value: string[]) => void;
    onBack: () => void;
    onNext: () => void;
};

export default function UnleashedIntegrationSettings({
    activeStep,
    steps,
    apiId,
    apiKey,
    integrationEnabled,
    canEnableIntegration,
    savingSettings,
    loadingProductGroups,
    loadingSalesOrderGroups,
    autoDisabledMessage,
    productGroups,
    selectedProductGroups,
    allProductsSelected,
    includeNonSellable,
    salesOrderGroups,
    selectedSalesOrderGroups,
    allSalesOrderGroupsSelected,
    onApiIdChange,
    onApiKeyChange,
    onToggleEnabled,
    onAllProductsChange,
    onProductGroupsChange,
    onIncludeNonSellableChange,
    onAllSalesOrderGroupsChange,
    onSalesOrderGroupsChange,
    onBack,
    onNext
}: UnleashedIntegrationSettingsProps) {
    const fieldId = useId();
    // Keep fields read-only until focused so password managers don't autofill on load.
    // Must be React state (not DOM removeAttribute) so controlled re-renders don't re-lock typing.
    const [apiIdReadOnly, setApiIdReadOnly] = useState(true);
    const [apiKeyReadOnly, setApiKeyReadOnly] = useState(true);

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <Box sx={{ width: '100%', position: 'relative' }}>
                        {/* Decoy login fields: LastPass targets these instead of API ID/Key. */}
                        <Box
                            aria-hidden="true"
                            sx={{
                                position: 'absolute',
                                left: '-9999px',
                                top: 0,
                                height: 0,
                                overflow: 'hidden',
                                opacity: 0,
                                pointerEvents: 'none'
                            }}
                        >
                            <input type="text" name="username" autoComplete="username" tabIndex={-1} />
                            <input type="password" name="password" autoComplete="current-password" tabIndex={-1} />
                        </Box>

                        <TextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            label="API ID"
                            // type=search avoids username/email heuristics in password managers
                            type="search"
                            name={`unleashed-api-id-${fieldId}`}
                            id={`unleashed-api-id-${fieldId}`}
                            value={apiId}
                            onChange={(event) => onApiIdChange(event.target.value)}
                            onFocus={() => setApiIdReadOnly(false)}
                            autoComplete="off"
                            inputProps={{
                                autoComplete: 'off',
                                'data-lpignore': 'true',
                                'data-form-type': 'other',
                                'data-1p-ignore': 'true',
                                'data-bwignore': 'true',
                                spellCheck: false,
                                readOnly: apiIdReadOnly
                            }}
                        />
                        <TextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            label="API Key"
                            type="search"
                            name={`unleashed-api-key-${fieldId}`}
                            id={`unleashed-api-key-${fieldId}`}
                            value={apiKey}
                            onChange={(event) => onApiKeyChange(event.target.value)}
                            onFocus={() => setApiKeyReadOnly(false)}
                            autoComplete="off"
                            inputProps={{
                                autoComplete: 'off',
                                'data-lpignore': 'true',
                                'data-form-type': 'other',
                                'data-1p-ignore': 'true',
                                'data-bwignore': 'true',
                                spellCheck: false,
                                readOnly: apiKeyReadOnly
                            }}
                        />
                    </Box>
                );
            case 1:
                return (
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <Card variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
                            <Typography
                                variant="subtitle1"
                                sx={{ mb: 1, textAlign: 'left', width: '100%' }}
                            >
                                Products
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 2, textAlign: 'left', width: '100%' }}
                            >
                                Choose which product groups to sync from Unleashed.
                            </Typography>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={allProductsSelected}
                                        onChange={(event) => onAllProductsChange(event.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="All Products"
                                sx={{ mb: 2, width: '100%' }}
                            />

                            {!allProductsSelected && (
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    disabled={loadingProductGroups}
                                    sx={{ mb: 2 }}
                                >
                                    <InputLabel id="product-groups-label">Product groups</InputLabel>
                                    <Select
                                        labelId="product-groups-label"
                                        multiple
                                        value={selectedProductGroups}
                                        onChange={(event) => onProductGroupsChange(typeof event.target.value === 'string'
                                            ? event.target.value.split(',')
                                            : event.target.value)}
                                        input={<OutlinedInput label="Product Groups" />}
                                        renderValue={(selected) => selected.join(', ')}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300
                                                }
                                            }
                                        }}
                                    >
                                        {loadingProductGroups ? (
                                            <MenuItem disabled>
                                                <CircularProgress size={24} />
                                                <Typography style={{ marginLeft: '10px' }}>Loading product groups...</Typography>
                                            </MenuItem>
                                        ) : (
                                            productGroups.map((group) => (
                                                <MenuItem key={group} value={group}>
                                                    <Checkbox checked={selectedProductGroups.indexOf(group) > -1} />
                                                    <ListItemText primary={group} />
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>
                            )}

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={includeNonSellable}
                                        onChange={(event) => onIncludeNonSellableChange(event.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Include non-sellable products"
                                sx={{ width: '100%' }}
                            />
                        </Card>

                        <Card variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
                            <Typography
                                variant="subtitle1"
                                sx={{ mb: 0.5, textAlign: 'left', width: '100%' }}
                            >
                                Sales order groups (optional)
                            </Typography>

                            {!loadingSalesOrderGroups && salesOrderGroups.length === 0 ? (
                                <Alert severity="info" sx={{ width: '100%' }}>
                                    No active sales order groups were found in Unleashed.
                                    Customer sync will continue without a sales order group filter
                                    (all customers, including obsolete).
                                </Alert>
                            ) : (
                                <>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 2, textAlign: 'left', width: '100%' }}
                                    >
                                        Optional. Select All Sales Order Groups to sync customers from every
                                        non-obsolete group, or choose specific groups below. If neither is selected,
                                        no sales order group filter is applied and all customers are synced
                                        (including obsolete).
                                    </Typography>

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={allSalesOrderGroupsSelected}
                                                onChange={(event) => onAllSalesOrderGroupsChange(event.target.checked)}
                                                color="primary"
                                                disabled={loadingSalesOrderGroups}
                                            />
                                        }
                                        label="All Sales Order Groups"
                                        sx={{ mb: 2, width: '100%' }}
                                    />

                                    {!allSalesOrderGroupsSelected && (
                                        <FormControl
                                            fullWidth
                                            variant="outlined"
                                            disabled={loadingSalesOrderGroups}
                                        >
                                            <InputLabel id="sales-order-groups-label">Sales order groups</InputLabel>
                                            <Select
                                                labelId="sales-order-groups-label"
                                                multiple
                                                value={selectedSalesOrderGroups}
                                                onChange={(event) => onSalesOrderGroupsChange(typeof event.target.value === 'string'
                                                    ? event.target.value.split(',')
                                                    : event.target.value)}
                                                input={<OutlinedInput label="Sales Order Groups" />}
                                                renderValue={(selected) => selected.join(', ')}
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: 300
                                                        }
                                                    }
                                                }}
                                            >
                                                {loadingSalesOrderGroups ? (
                                                    <MenuItem disabled>
                                                        <CircularProgress size={24} />
                                                        <Typography style={{ marginLeft: '10px' }}>Loading sales order groups...</Typography>
                                                    </MenuItem>
                                                ) : (
                                                    salesOrderGroups.map((group) => (
                                                        <MenuItem key={group} value={group}>
                                                            <Checkbox checked={selectedSalesOrderGroups.indexOf(group) > -1} />
                                                            <ListItemText primary={group} />
                                                        </MenuItem>
                                                    ))
                                                )}
                                            </Select>
                                        </FormControl>
                                    )}
                                </>
                            )}
                        </Card>
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <>
            {autoDisabledMessage && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography>Integration was automatically disabled:</Typography>
                    <Typography sx={{ fontWeight: 'bold', my: 1 }}>{autoDisabledMessage}</Typography>
                    <Typography>Enable the integration again after resolving the issue.</Typography>
                </Alert>
            )}

            {canEnableIntegration && (
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: 3 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={integrationEnabled}
                                onChange={(event) => onToggleEnabled(event.target.checked)}
                                color="primary"
                                disabled={savingSettings}
                            />
                        }
                        label="Enabled"
                    />
                    {savingSettings && (
                        <CircularProgress size={20} sx={{ ml: 1 }} />
                    )}
                </Box>
            )}

            <Typography variant="subtitle1" sx={{ width: '100%', mb: 2 }}>
                Unleashed configuration
            </Typography>

            <Divider style={{ width: '100%', marginBottom: '16px' }} />

            <Stepper activeStep={activeStep} style={{ width: '100%', marginBottom: '20px' }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {renderStepContent()}

            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                    color="inherit"
                    disabled={activeStep === 0}
                    onClick={onBack}
                    sx={{ mr: 1 }}
                >
                    Back
                </Button>
                <Box sx={{ flex: '1 1 auto' }} />
                {(loadingProductGroups || loadingSalesOrderGroups) && (
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                )}
                <Button
                    onClick={onNext}
                    variant="contained"
                    color="primary"
                    disabled={
                        ((!apiId || !apiKey) && activeStep === 0) ||
                        (activeStep === 1 && !allProductsSelected && selectedProductGroups.length === 0) ||
                        loadingProductGroups ||
                        loadingSalesOrderGroups ||
                        savingSettings
                    }
                >
                    {activeStep === steps.length - 1 ? 'Save' : 'Next'}
                </Button>
            </Box>
        </>
    );
}
